import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import {
	AlertCircleIcon,
	CheckIcon,
	ChevronsUpDownIcon,
	ExternalLinkIcon,
	LoaderIcon,
	MapPinIcon,
	TriangleAlertIcon,
} from "lucide-react";
import {
	useCallback,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState,
} from "react";
import type { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import HereMap, { type HereMapProps } from "@/components/shared/here-map";
import { Button } from "@/components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { MAP_DEFAULT_COORDINATE } from "@/lib/here-maps";
import { cn } from "@/lib/utils";
import type { Location } from "@/types/admin-portal/location";

export const ADDRESS_ENTRY_SCHEMA = z.object({
	address: z.string().min(1, "Address is required"),
	postalCode: z.string().optional(),
	coordinate: z.string().optional(),
	locationId: z.coerce.number().min(1, "Location is required"),
	notes: z.string().optional(),
});
export type AddressEntryValues = z.infer<typeof ADDRESS_ENTRY_SCHEMA>;

// "lat, lng" as typed or geocoded; null unless both halves are numbers.
export function parseCoordinate(value?: string | null) {
	if (!value) return null;
	const [lat, lng] = value.split(",").map((s) => parseFloat(s.trim()));
	return Number.isNaN(lat) || Number.isNaN(lng) ? null : { lat, lng };
}

// The patient form's own English copy; other callers pass translated labels.
const DEFAULT_LABELS = {
	region: "Region",
	regionPlaceholder: "Select region",
	regionSearch: "Search region by city...",
	regionEmpty: "No region found.",
	optional: "optional",
	postalCode: "Postal Code",
	postalCodePlaceholder: "Enter postal code",
	address: "Address",
	addressPlaceholder: "Enter complete address",
	notes: "Notes",
	notesPlaceholder: "Additional notes about this address",
	coordinate: "Coordinate",
	coordinateRequired: "",
	coordinatePlaceholder: "Coordinates (e.g., -6.1944,106.8229)",
	manual: "Manual",
	automatic: "Automatic",
	geocode: "Get Coordinates from Address",
	geocoding: "Geocoding...",
	reset: "Reset",
	viewOnMaps: "View on Maps",
	geocodeNeedsAddress: "Please enter an address before geocoding",
	geocodeNeedsRegion: "Please select a region before geocoding",
	geocodeSuccess: "Coordinates calculated successfully",
	geocodeNotFound: "Address not found, please check the address and try again",
	geocodeFailed: "Failed to calculate coordinates",
	geocodeUnavailable: "Here Maps geocoding not available",
	pinStale: "",
	pinRefetch: "",
};
export type AddressEntryLabels = typeof DEFAULT_LABELS;

// The Update Address dialog's translated copy, shared by the admin forms that reuse these fields.
export function useAddressEntryLabels() {
	const { t, ready } = useTranslation("appointments", {
		keyPrefix: "form.update_address",
	});
	// biome-ignore lint/correctness/useExhaustiveDependencies: t keeps its identity when the namespace loads late, so ready recomputes the labels
	return useMemo<Partial<AddressEntryLabels>>(
		() => ({
			region: t("fields.location.label"),
			regionPlaceholder: t("fields.location.placeholder"),
			regionSearch: t("fields.location.search"),
			regionEmpty: t("fields.location.empty"),
			optional: t("fields.optional"),
			postalCode: t("fields.postal_code.label"),
			postalCodePlaceholder: t("fields.postal_code.placeholder"),
			address: t("fields.address_line.label"),
			addressPlaceholder: t("fields.address_line.placeholder"),
			notes: t("fields.notes.label"),
			notesPlaceholder: t("fields.notes.placeholder"),
			coordinate: t("fields.coordinate.label"),
			coordinateRequired: t("fields.coordinate.required"),
			coordinatePlaceholder: t("fields.coordinate.placeholder"),
			manual: t("fields.coordinate.manual"),
			automatic: t("fields.coordinate.automatic"),
			geocode: t("geocode.button"),
			geocoding: t("geocode.loading"),
			reset: t("geocode.reset"),
			viewOnMaps: t("geocode.view"),
			geocodeNeedsAddress: t("geocode.need_address"),
			geocodeNeedsRegion: t("geocode.need_location"),
			geocodeSuccess: t("geocode.success"),
			geocodeNotFound: t("geocode.not_found"),
			geocodeFailed: t("geocode.failed"),
			geocodeUnavailable: t("geocode.unavailable"),
			pinStale: t("pin.stale"),
			pinRefetch: t("pin.refetch"),
		}),
		[t, ready],
	);
}

// The address-entry fields shared by the patient address drawer and the Update Address
// dialog: region, postal code, address, notes, a manual or HERE-geocoded coordinate, and
// the map. The caller owns the form, its schema and its submit.
export function AddressEntryFields({
	form,
	locations,
	labels: labelsProp,
	confirmCoordinateReset = false,
	onGeocodingChange,
	className,
	notesClassName = "min-h-0",
	mapHeight = "400px",
}: {
	form: UseFormReturn<AddressEntryValues>;
	locations: Pick<Location, "id" | "city" | "state" | "country">[];
	labels?: Partial<AddressEntryLabels>;
	// true: editing a field once a pin exists asks to clear the pin first (patient form).
	// false: the edit goes through and a hint says the pin may no longer match.
	confirmCoordinateReset?: boolean;
	onGeocodingChange?: (isGeocoding: boolean) => void;
	className?: string;
	notesClassName?: string;
	mapHeight?: string;
}) {
	const labels = { ...DEFAULT_LABELS, ...labelsProp };
	const coordinateFieldId = useId();
	const [locationPopoverOpen, setLocationPopoverOpen] = useState(false);
	const hereMapRef = useRef<any>(null);
	const [latLngInputMethod, setLatLngInputMethod] = useState<
		"manual" | "automatic"
	>("manual");
	const [isGeocoding, setIsGeocoding] = useState(false);
	const [geocodingError, setGeocodingError] = useState<string | null>(null);

	useEffect(() => {
		onGeocodingChange?.(isGeocoding);
	}, [isGeocoding, onGeocodingChange]);

	const coordinate = form.watch("coordinate");
	const address = form.watch("address");
	const locationId = form.watch("locationId");
	const postalCode = form.watch("postalCode");

	// The pin belongs to the address it was set for; a new coordinate re-anchors it.
	const pinSource = [address, locationId, postalCode].join("|");
	const [pin, setPin] = useState({ coordinate, source: pinSource });
	if (pin.coordinate !== coordinate) setPin({ coordinate, source: pinSource });
	// Setting a pin anchors it to the current address even when the coordinate is unchanged.
	const anchorPin = useCallback(
		(value: string) =>
			setPin({
				coordinate: value,
				source: [
					form.getValues("address"),
					form.getValues("locationId"),
					form.getValues("postalCode"),
				].join("|"),
			}),
		[form],
	);
	const isPinStale =
		!confirmCoordinateReset && !!coordinate?.trim() && pin.source !== pinSource;

	const handleFieldChange = (
		fieldName: "locationId" | "postalCode" | "address",
		newValue: any,
	) => {
		if (confirmCoordinateReset && coordinate && coordinate.trim() !== "") {
			const confirmReset = window.confirm(
				"Warning: Changing this field will reset the coordinates. Do you want to continue?",
			);
			if (!confirmReset) {
				return;
			}
			form.setValue("coordinate", "");
		}
		form.setValue(fieldName, newValue, {
			shouldValidate: !confirmCoordinateReset,
		});
	};

	// Group locations by country and state for the dropdown, flattened for search
	const { groupedLocations, flattenedLocations } = useMemo(() => {
		const grouped = locations.reduce(
			(countries, location) => {
				const country = location.country || "Unknown Country";
				const state = location.state || "Unknown State";
				countries[country] ??= {};
				countries[country][state] ??= [];
				countries[country][state].push(location);
				return countries;
			},
			{} as Record<string, Record<string, typeof locations>>,
		);

		const flattened = locations.map((location) => ({
			...location,
			displayText:
				`${location.city}, ${location.state || ""}, ${location.country || ""}`
					.replace(/,\s*,/g, ",")
					.replace(/,$/, ""),
		}));

		return { groupedLocations: grouped, flattenedLocations: flattened };
	}, [locations]);

	const fullAddressOf = useCallback(
		(line: string, id: number) => {
			const location = flattenedLocations.find((loc) => loc.id === id);
			return location
				? `${line}, ${location.city}, ${location.state}, ${location.country}`
				: line;
		},
		[flattenedLocations],
	);

	const hereMapProps = useMemo(() => {
		const parsed = parseCoordinate(coordinate);
		const location = flattenedLocations.find((loc) => loc.id === locationId);

		return {
			coordinate: parsed ? [parsed.lat, parsed.lng] : MAP_DEFAULT_COORDINATE,
			address: {
				address: address || "",
				city: location?.city || "",
				state: location?.state || "",
				country: location?.country || "",
				postalCode: postalCode || "",
			},
			options: { disabledEvent: true },
			className: "w-full",
			height: mapHeight,
		} satisfies HereMapProps;
	}, [
		coordinate,
		address,
		locationId,
		postalCode,
		flattenedLocations,
		mapHeight,
	]);

	// Add marker to map when coordinates are available
	useEffect(() => {
		const parsed = parseCoordinate(coordinate);
		if (!parsed || !hereMapRef.current?.marker?.onAdd) return;

		hereMapRef.current.marker.onAdd(
			[
				{
					position: parsed,
					address: fullAddressOf(
						form.getValues("address"),
						form.getValues("locationId"),
					),
				},
			],
			{ changeMapView: true },
		);

		return () => {
			if (hereMapRef.current?.marker?.onRemove) {
				hereMapRef.current.marker.onRemove();
			}
		};
	}, [coordinate, form, fullAddressOf]);

	const handleResetCoordinates = useCallback(() => {
		form.setValue("coordinate", "", { shouldValidate: true });
		setGeocodingError(null);

		if (hereMapRef.current?.marker?.onRemove) {
			hereMapRef.current.marker.onRemove();
		}
	}, [form]);

	const handleViewOnGoogleMaps = useCallback(() => {
		const parsed = parseCoordinate(form.getValues("coordinate"));
		if (parsed) {
			window.open(
				`https://www.google.com/maps/search/?api=1&query=${parsed.lat},${parsed.lng}`,
				"_blank",
			);
		}
	}, [form]);

	const handleGeocoding = useCallback(async () => {
		const currentAddress = form.getValues("address");
		const currentLocationId = form.getValues("locationId");
		setGeocodingError(null);

		const fail = (message: string) => {
			setGeocodingError(message);
			toast.error(message);
		};

		if (!currentAddress?.trim()) return fail(labels.geocodeNeedsAddress);
		if (!currentLocationId) return fail(labels.geocodeNeedsRegion);
		if (!hereMapRef.current?.geocode?.onCalculate) {
			return fail(labels.geocodeUnavailable);
		}

		setIsGeocoding(true);
		try {
			const result = await hereMapRef.current.geocode.onCalculate();
			if (!result?.position) throw new Error("No results found");

			const { lat, lng } = result.position;
			form.setValue("coordinate", `${lat}, ${lng}`, { shouldValidate: true });
			anchorPin(`${lat}, ${lng}`);
			hereMapRef.current?.marker?.onAdd?.(
				[
					{
						position: { lat, lng },
						address: fullAddressOf(currentAddress, currentLocationId),
					},
				],
				{ changeMapView: true },
			);
			toast.success(labels.geocodeSuccess);
		} catch (error) {
			console.error("Geocoding failed:", error);
			fail(
				error instanceof Error && error.message.includes("No results found")
					? labels.geocodeNotFound
					: labels.geocodeFailed,
			);
		} finally {
			setIsGeocoding(false);
		}
	}, [
		form,
		anchorPin,
		fullAddressOf,
		labels.geocodeNeedsAddress,
		labels.geocodeNeedsRegion,
		labels.geocodeUnavailable,
		labels.geocodeSuccess,
		labels.geocodeNotFound,
		labels.geocodeFailed,
	]);

	const optional = (
		<span className="ml-1.5 text-xs font-normal text-muted-foreground">
			{labels.optional}
		</span>
	);
	const isAutomatic = latLngInputMethod === "automatic";

	return (
		<div className={className}>
			<div className="min-w-0 space-y-4">
				{/* Region and postal code share a row while there is room, and stack when there isn't. */}
				<div className="flex flex-wrap items-start gap-4">
					<FormField
						control={form.control}
						name="locationId"
						render={({ field }) => (
							<FormItem className="min-w-0 flex-[1_1_14rem]">
								<FormLabel>{labels.region}</FormLabel>
								<Popover
									modal
									open={locationPopoverOpen}
									onOpenChange={setLocationPopoverOpen}
								>
									<PopoverTrigger asChild>
										<FormControl>
											<Button
												type="button"
												variant="outline"
												className={cn(
													"w-full min-w-0 justify-between bg-input shadow-inner",
													!field.value && "text-muted-foreground",
												)}
											>
												<span className="min-w-0 truncate">
													{(field.value &&
														flattenedLocations.find(
															(location) => location.id === field.value,
														)?.displayText) ||
														labels.regionPlaceholder}
												</span>
												<ChevronsUpDownIcon className="size-4 shrink-0 opacity-50" />
											</Button>
										</FormControl>
									</PopoverTrigger>
									<PopoverContent
										className="w-[var(--radix-popover-trigger-width)] min-w-64 p-0"
										align="start"
									>
										<Command>
											<CommandInput placeholder={labels.regionSearch} />
											<CommandList>
												<CommandEmpty>{labels.regionEmpty}</CommandEmpty>

												{Object.entries(groupedLocations).map(
													([country, states]) => (
														<div key={country}>
															<div className="px-2 py-1.5 text-sm font-bold text-muted-foreground">
																{country}
															</div>
															{Object.entries(states).map(([state, cities]) => (
																<CommandGroup
																	key={state}
																	heading={`└ ${state}`}
																>
																	{cities.map((location) => (
																		<CommandItem
																			key={location.id}
																			value={location.city}
																			onSelect={() => {
																				handleFieldChange(
																					"locationId",
																					location.id,
																				);
																				setLocationPopoverOpen(false);
																			}}
																		>
																			<span>{location.city}</span>

																			<CheckIcon
																				className={cn(
																					"ml-auto",
																					location.id === field.value
																						? "opacity-100"
																						: "opacity-0",
																				)}
																			/>
																		</CommandItem>
																	))}
																</CommandGroup>
															))}
														</div>
													),
												)}
											</CommandList>
										</Command>
									</PopoverContent>
								</Popover>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="postalCode"
						render={({ field }) => (
							<FormItem className="min-w-0 flex-[0_1_9rem]">
								<FormLabel>
									{labels.postalCode}
									{optional}
								</FormLabel>
								<FormControl>
									<Input
										{...field}
										inputMode="numeric"
										onChange={(e) =>
											handleFieldChange("postalCode", e.target.value)
										}
										className="w-full bg-input shadow-inner tabular-nums"
										placeholder={labels.postalCodePlaceholder}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<FormField
					control={form.control}
					name="address"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{labels.address}</FormLabel>
							<FormControl>
								<Textarea
									{...field}
									onChange={(e) => handleFieldChange("address", e.target.value)}
									className="min-h-24 max-w-full resize-y bg-input py-1.75 shadow-inner field-sizing-content"
									placeholder={labels.addressPlaceholder}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="notes"
					render={({ field }) => (
						<FormItem>
							<FormLabel>
								{labels.notes}
								{optional}
							</FormLabel>
							<FormControl>
								<Textarea
									{...field}
									className={cn(
										"max-w-full resize-y bg-input py-1.75 shadow-inner field-sizing-content",
										notesClassName,
									)}
									placeholder={labels.notesPlaceholder}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
			</div>

			<div className="min-w-0 space-y-3">
				<div className="flex flex-wrap items-center justify-between gap-2">
					<span
						id={`${coordinateFieldId}-label`}
						className="text-sm font-medium leading-none"
					>
						{labels.coordinate}
						{labels.coordinateRequired && (
							<span className="ml-1.5 text-xs font-normal text-muted-foreground">
								{labels.coordinateRequired}
							</span>
						)}
					</span>

					{/* How the pin is set: typed in, or looked up from the address. */}
					<RadioGroupPrimitive.Root
						value={latLngInputMethod}
						onValueChange={(value) =>
							setLatLngInputMethod(value as "manual" | "automatic")
						}
						aria-labelledby={`${coordinateFieldId}-label`}
						orientation="horizontal"
						className="inline-flex rounded-md border bg-muted p-0.5"
					>
						{(["manual", "automatic"] as const).map((method) => (
							<RadioGroupPrimitive.Item
								key={method}
								value={method}
								className="rounded-sm px-3 py-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:translate-y-px disabled:pointer-events-none disabled:opacity-50 data-[state=checked]:bg-background data-[state=checked]:text-foreground data-[state=checked]:shadow-sm"
							>
								{labels[method]}
							</RadioGroupPrimitive.Item>
						))}
					</RadioGroupPrimitive.Root>
				</div>

				<FormField
					control={form.control}
					name="coordinate"
					render={({ field }) => (
						<FormItem>
							<FormControl>
								<Input
									{...field}
									value={field.value || ""}
									onChange={(e) => {
										field.onChange(e.target.value.trim());
										anchorPin(e.target.value.trim());
									}}
									aria-labelledby={`${coordinateFieldId}-label`}
									className={cn(
										"bg-input font-mono text-sm tabular-nums shadow-inner",
										isAutomatic && "cursor-default bg-muted",
									)}
									placeholder={labels.coordinatePlaceholder}
									readOnly={isAutomatic}
									{...(isAutomatic && { tabIndex: -1 })}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				{isAutomatic && (
					<div className="flex flex-wrap items-center gap-2">
						<Button
							type="button"
							variant="primary-outline"
							size="sm"
							onClick={handleGeocoding}
							disabled={isGeocoding || !address}
							className="min-w-0"
						>
							{isGeocoding ? (
								<LoaderIcon className="animate-spin" />
							) : (
								<MapPinIcon />
							)}
							<span className="truncate">
								{isGeocoding ? labels.geocoding : labels.geocode}
							</span>
						</Button>

						{coordinate && (
							<>
								<Button
									type="button"
									variant="ghost"
									size="sm"
									disabled={isGeocoding}
									onClick={handleViewOnGoogleMaps}
								>
									<ExternalLinkIcon />
									{labels.viewOnMaps}
								</Button>
								<Button
									type="button"
									variant="ghost-destructive"
									size="sm"
									disabled={isGeocoding}
									onClick={handleResetCoordinates}
								>
									{labels.reset}
								</Button>
							</>
						)}
					</div>
				)}

				{isPinStale && (
					<p className="flex flex-wrap items-start gap-x-2 gap-y-1 rounded-md bg-amber-50/70 px-3 py-2 text-xs text-amber-800">
						<TriangleAlertIcon className="mt-px size-3.5 shrink-0 text-amber-600" />
						<span className="min-w-0 flex-1">{labels.pinStale}</span>
						<button
							type="button"
							disabled={isGeocoding}
							className="shrink-0 rounded-sm font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
							onClick={() => {
								setLatLngInputMethod("automatic");
								handleGeocoding();
							}}
						>
							{labels.pinRefetch}
						</button>
					</p>
				)}

				{geocodingError && (
					<p
						role="alert"
						className="flex items-start gap-1.5 text-xs text-destructive"
					>
						<AlertCircleIcon className="mt-px size-3.5 shrink-0" />
						<span className="min-w-0">{geocodingError}</span>
					</p>
				)}

				<div className="w-full max-w-full overflow-hidden rounded-md border">
					<HereMap ref={hereMapRef} {...hereMapProps} />
				</div>
			</div>
		</div>
	);
}
